import React from 'react';

import { ImperativePanelHandle, Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import LayoutHandler from './LayoutHandler';
import UnityView from '../unityView/UnityView';
import Configurator from '../configurator/Configurator';
import InstantCatalog from './common/InstantCatalog';
import UnifiedInstacartHeader from './unified/UnifiedInstacartHeader';
import UnifiedInstacartContent from './unified/UnifiedInstacartContent';
import QuoteBar from './unified/QuoteBar';
import IShopInfo from "../unityView/interfaces/IShopInfo";
import { CartContext } from '../cart/CartHandler';
import PrestaGlobals from '../common/PrestaGlobals';

interface UnifiedLayoutProps {
    shopInfo: IShopInfo;
    UCOnLoadedReceived: () => void;
    UCOrderReceived: (result: any) => void;
    UCViewChangedReceived: (key: string, value: string) => void;
    /** What sits in the configurator's place; the Configurator when left out. */
    sidePanel?: React.ReactNode;
}

const UnifiedLayout: React.FC<UnifiedLayoutProps> = ({ shopInfo, UCOnLoadedReceived, UCOrderReceived, UCViewChangedReceived, sidePanel }) => {
    const theme = useTheme();
    const { isQuote } = React.useContext(CartContext);
    // Matches UnifiedInstacartContent's breakpoint — both switch at md
    const isMobileView = useMediaQuery(theme.breakpoints.down('md'));

    // Desktop: ref on the configurator box for LayoutHandler to observe its width
    const desktopBoxRef = React.useRef<HTMLElement>(null);
    const instacartPanelRef = React.useRef<ImperativePanelHandle>(null);
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [handleDisabled, setHandleDisabled] = React.useState(false);

    // Mobile: ref on the configurator wrapper — same purpose, full-width element
    const mobileBoxRef = React.useRef<HTMLElement>(null);

    const onHorizontalLayout = (sizes: number[]) => {
        document.cookie = `react-resizable-panels:horizontal-layout=${JSON.stringify(sizes)}`;
    };

    const onVerticalLayout = (sizes: number[]) => {
        document.cookie = `react-resizable-panels:vertical-layout=${JSON.stringify(sizes)}`;
    };

    const handleCollapseToggle = () => {
        setHandleDisabled(false);
        if (instacartPanelRef.current == null) return;
        if (instacartPanelRef.current.isCollapsed() || instacartPanelRef.current.getSize() < 10) {
            instacartPanelRef.current.expand();
            if (instacartPanelRef.current.getSize() < 10) {
                instacartPanelRef.current.resize(20);
            }
        } else {
            instacartPanelRef.current.collapse();
        }
    };

    // Quote cart: Unity view only
    if (isQuote && !PrestaGlobals.isAdmin) {
        return (
            <LayoutHandler>
                <Box style={{ height: 'var(--app-height, 100dvh)', width: '100vw', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                        <UnityView
                            shopInfo={shopInfo}
                            UCOnLoadedReceived={UCOnLoadedReceived}
                            UCOrderReceived={UCOrderReceived}
                            UCViewChangedReceived={UCViewChangedReceived}
                        />
                    </Box>
                    <QuoteBar />
                </Box>
            </LayoutHandler>
        );
    }

    if (isMobileView) {
        return (
            <LayoutHandler ref={mobileBoxRef}>
                <Stack direction="column" spacing={0} style={{ width: '100vw', overflow: 'auto' }}>
                    <Box style={{ height: '50vh' }}>
                        <UnityView
                            shopInfo={shopInfo}
                            UCOnLoadedReceived={UCOnLoadedReceived}
                            UCOrderReceived={UCOrderReceived}
                            UCViewChangedReceived={UCViewChangedReceived}
                        />
                    </Box>
                    <Box ref={mobileBoxRef}>
                        {sidePanel ?? <Configurator isMobile={true} />}
                    </Box>
                    <UnifiedInstacartHeader />
                    <UnifiedInstacartContent />
                    <InstantCatalog />
                </Stack>
            </LayoutHandler>
        );
    }

    return (
        <LayoutHandler ref={desktopBoxRef}>
            <PanelGroup
                direction="vertical"
                onLayout={onVerticalLayout}
                autoSaveId="react-resizable-panels:vertical-layout"
                style={{ height: 'var(--app-height, 100dvh)', width: '100vw' }}
            >
                <Panel defaultSize={85} minSize={30}>
                    <PanelGroup
                        direction="horizontal"
                        onLayout={onHorizontalLayout}
                        autoSaveId="react-resizable-panels:horizontal-layout"
                    >
                        <Panel defaultSize={70} minSize={30}>
                            <UnityView
                                shopInfo={shopInfo}
                                UCOnLoadedReceived={UCOnLoadedReceived}
                                UCOrderReceived={UCOrderReceived}
                                UCViewChangedReceived={UCViewChangedReceived}
                            />
                        </Panel>
                        <PanelResizeHandle style={{ background: theme.palette.divider, width: '5px', cursor: 'col-resize' }} />
                        <Panel defaultSize={30} minSize={30} style={{ minWidth: 375 }}>
                            <Box ref={desktopBoxRef} height="100%" width="100%">
                                {sidePanel ?? <Configurator isMobile={false} />}
                            </Box>
                        </Panel>
                    </PanelGroup>
                </Panel>

                <PanelResizeHandle
                    style={{ background: theme.palette.divider}}
                    disabled={handleDisabled}
                >
                    <UnifiedInstacartHeader
                        onCollapseToggle={handleCollapseToggle}
                        isCollapsed={isCollapsed}
                        onHandleDisabledChange={setHandleDisabled}
                    />
                </PanelResizeHandle>

                <Panel
                    defaultSize={20}
                    minSize={0}
                    collapsible
                    ref={instacartPanelRef}
                    onCollapse={() => setIsCollapsed(true)}
                    onExpand={() => setIsCollapsed(false)}
                >
                    <UnifiedInstacartContent />
                </Panel>
            </PanelGroup>
            <InstantCatalog />
        </LayoutHandler>
    );
};

export default UnifiedLayout;